// One delivery (a new task, a nudge, a reminder, a message, the end-of-day prompt or the evening
// brief) as a card with its actions: inside a desktop toast, and at the top of My tasks on both
// editions. A message shows what was written and the shared quick-reply row. Pure: every action is a
// callback; what this session did to it arrives in `status`. Task buttons come only from the task's
// `allowedActions`.
import { Button, IconButton } from '@affine/component';
import { type I18nInstance, useI18n } from '@affine/i18n';
import { CloseIcon } from '@blocksuite/icons/rc';
import { type KeyboardEvent, useId } from 'react';

import type {
  DirectorDelivery,
  DirectorMessagePayload,
  DirectorTask,
} from '../../../../../modules/erp/director-tasks-types';
import {
  cadenceUpdatesPhrase,
  categoryLabel,
  formatDay,
  relativeShort,
} from '../model/format';
import { QuickReply, type QuickReplyProps } from '../ui/quick-reply';
import * as ui from '../ui/ui.css';
import * as styles from './my-tasks-view.css';

/** How long "Accepted · … notified" (or "Question sent" in a toast) stays before the card goes. */
export const CONFIRMATION_MS = 3_000;

/** `open` follows the card's main link: the task, Today's updates, or the Inbox. */
export type DeliveryAction = 'accept' | 'ask' | 'open' | 'dismiss';

/** What happened to a card or a task row in this session. */
export interface ItemStatus {
  accepted?: boolean;
  asked?: boolean;
  nudged?: boolean;
  notice?: { kind: 'conflict' } | { kind: 'error'; message: string };
}

/** The inline question box of the one card or row that has it open. */
export interface AskBoxProps {
  draft: string;
  onChange: (text: string) => void;
  onSend: () => void;
  onCancel: () => void;
}

/** How a message card's quick reply is sent, and what happens after (the caller's choice). */
export type DeliveryReplyProps = Pick<
  QuickReplyProps,
  'send' | 'onSent' | 'onEscape'
>;

export interface DeliveryCardProps {
  delivery: DirectorDelivery;
  now: Date;
  pending: boolean;
  status?: ItemStatus;
  ask: AskBoxProps | null;
  /** A message's quick reply; without it a message card offers Open only. */
  reply?: DeliveryReplyProps | null;
  onAction: (delivery: DirectorDelivery, action: DeliveryAction) => void;
}

/** The task a delivery is about (a new task or a message); `null` for the daily items. */
export function deliveryTask(delivery: DirectorDelivery): DirectorTask | null {
  return delivery.kind === 'newTask' || delivery.kind === 'message'
    ? delivery.task
    : null;
}

/** "Alex Rivera" → "Alex"; no name → "Someone". */
export function firstName(name: string | null, t: I18nInstance): string {
  const first = (name ?? '').trim().split(/\s+/)[0];
  return first || t['com.affine.pipe.director.tasks.inbox.someone']();
}

const SUMMARY_TITLE_SEPARATOR = ': ';

/** A message card's heading: who wrote on which task, and why. */
function messageTitle(
  payload: DirectorMessagePayload,
  title: string,
  t: I18nInstance
): string {
  const name =
    payload.authorName ?? t['com.affine.pipe.director.tasks.inbox.someone']();
  switch (payload.reason) {
    case 'question':
      return t['com.affine.pipe.director.tasks.delivery.message.question']({
        name,
        title,
      });
    case 'blocked':
      return t['com.affine.pipe.director.tasks.delivery.message.blocked']({
        name,
        title,
      });
    default:
      return t['com.affine.pipe.director.tasks.delivery.message.comment']({
        name,
        title,
      });
  }
}

/** The card's heading. A nudge may come from a site or super admin rather than the assigner, so its
 *  sender is taken from the server's summary ("Nudge from X: <title>"), less the repeated title. */
export function deliveryTitle(
  delivery: DirectorDelivery,
  t: I18nInstance
): string {
  if (delivery.kind === 'message')
    return messageTitle(delivery.payload, delivery.task.title, t);
  if (delivery.kind !== 'newTask') return delivery.summary;
  switch (delivery.payload.reason) {
    case 'new':
      return t['com.affine.pipe.director.tasks.delivery.new']({
        name:
          delivery.task.assignedByName ??
          t['com.affine.pipe.director.tasks.inbox.someone'](),
      });
    case 'autoNudge':
      return t['com.affine.pipe.director.tasks.delivery.reminder']();
    case 'nudge': {
      const suffix = `${SUMMARY_TITLE_SEPARATOR}${delivery.task.title}`;
      return delivery.summary.endsWith(suffix)
        ? delivery.summary.slice(0, -suffix.length)
        : delivery.summary;
    }
    default:
      return delivery.summary;
  }
}

/** What was written: up to four lines (the task holds the rest) and how many earlier unread
 *  messages it replaced. A blocking update without a note has no text. */
function MessageText({ payload }: { payload: DirectorMessagePayload }) {
  const t = useI18n();
  return (
    <>
      {payload.text !== null && (
        <p className={styles.messageText} data-testid="delivery-message">
          {payload.text}
        </p>
      )}
      {payload.priorCount > 0 && (
        <span className={styles.cardMeta}>
          {t['com.affine.pipe.director.tasks.delivery.message.earlier']({
            count: payload.priorCount,
          })}
        </span>
      )}
    </>
  );
}

/** "Equipment procurement · due Fri, Oct 4 · updates Daily". */
export function taskMeta(task: DirectorTask, now: Date, t: I18nInstance) {
  const category = categoryLabel(task.category, t);
  const updates = cadenceUpdatesPhrase(task.updateCadence, t);
  return task.dueAt
    ? t['com.affine.pipe.director.tasks.delivery.meta']({
        category,
        due: formatDay(task.dueAt, now, t, undefined, true),
        updates,
      })
    : t['com.affine.pipe.director.tasks.delivery.meta-undated']({
        category,
        updates,
      });
}

export function AskBox({
  to,
  ask,
  pending,
}: {
  /** Who the question goes to. */
  to: string;
  ask: AskBoxProps;
  pending: boolean;
}) {
  const t = useI18n();
  const id = useId();
  const empty = ask.draft.trim() === '';
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !empty) {
      event.preventDefault();
      ask.onSend();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      ask.onCancel();
    }
  };
  return (
    <div className={styles.ask}>
      <label htmlFor={id} className={styles.askLabel}>
        {t['com.affine.pipe.director.tasks.mine.ask.label']({ name: to })}
      </label>
      <div className={styles.askLine}>
        <input
          id={id}
          autoFocus
          className={styles.askInput}
          value={ask.draft}
          placeholder={t[
            'com.affine.pipe.director.tasks.mine.ask.placeholder'
          ]()}
          onChange={event => ask.onChange(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <Button variant="plain" onClick={ask.onCancel}>
          {t['com.affine.pipe.director.tasks.mine.ask.cancel']()}
        </Button>
        <Button
          variant="primary"
          disabled={empty || pending}
          onClick={ask.onSend}
        >
          {t['com.affine.pipe.director.tasks.mine.ask.send']()}
        </Button>
      </div>
    </div>
  );
}

export function ItemNotice({ status }: { status: ItemStatus | undefined }) {
  const t = useI18n();
  const notice = status?.notice;
  if (notice?.kind === 'conflict')
    return (
      <p role="status" className={styles.notice}>
        {t['com.affine.pipe.director.tasks.mine.conflict']()}
      </p>
    );
  if (notice)
    return (
      <p role="alert" className={styles.noticeError}>
        {t['com.affine.pipe.director.tasks.mine.error']({
          message: notice.message,
        })}
      </p>
    );
  if (status?.asked)
    return (
      <p role="status" className={styles.notice}>
        {t['com.affine.pipe.director.tasks.mine.asked']()}
      </p>
    );
  if (status?.nudged)
    return (
      <p role="status" className={styles.notice}>
        {t['com.affine.pipe.director.tasks.inbox.nudged']()}
      </p>
    );
  return null;
}

function openLabel(delivery: DirectorDelivery, t: I18nInstance) {
  switch (delivery.kind) {
    case 'newTask':
      return t['com.affine.pipe.director.tasks.delivery.open']();
    case 'message':
      return t['com.affine.pipe.director.tasks.reply.open-task']();
    case 'eodPrompt':
      return t['com.affine.pipe.director.tasks.delivery.update-now']();
    case 'eveningBrief':
      return t['com.affine.pipe.director.tasks.delivery.open-inbox']();
  }
}

export function DeliveryCard({
  delivery,
  now,
  pending,
  status,
  ask,
  reply,
  onAction,
}: DeliveryCardProps) {
  const t = useI18n();
  const task = deliveryTask(delivery);
  const message = delivery.kind === 'message' ? delivery.payload : null;
  if (task && status?.accepted)
    return (
      <div role="status" className={styles.confirmation}>
        {t['com.affine.pipe.director.tasks.delivery.accepted']({
          name: firstName(task.assignedByName, t),
        })}
      </div>
    );
  // A message card replies; accepting or asking stay on the task's own notice and row.
  const allowed = new Set(message ? [] : (task?.allowedActions ?? []));
  const button = (action: DeliveryAction, label: string, primary = false) => (
    <Button
      key={action}
      variant={primary ? 'primary' : 'secondary'}
      className={styles.cardButton}
      disabled={pending}
      onClick={() => onAction(delivery, action)}
    >
      {label}
    </Button>
  );
  return (
    <article
      className={styles.card}
      data-testid="delivery-card"
      aria-label={t['com.affine.pipe.director.tasks.delivery.label']()}
    >
      <div className={styles.cardTop}>
        <span className={styles.cardHeading}>{deliveryTitle(delivery, t)}</span>
        <span>{relativeShort(delivery.createdAt, now, t)}</span>
        <IconButton
          size="16"
          className={styles.dismiss}
          aria-label={t['com.affine.pipe.director.tasks.delivery.dismiss']()}
          disabled={pending}
          onClick={() => onAction(delivery, 'dismiss')}
        >
          <CloseIcon />
        </IconButton>
      </div>
      {message && <MessageText payload={message} />}
      {task && !message && (
        <>
          <p className={styles.cardTitle}>{task.title}</p>
          <span className={styles.cardMeta}>{taskMeta(task, now, t)}</span>
        </>
      )}
      {delivery.kind === 'eveningBrief' &&
        delivery.payload.lines.length > 0 && (
          <ul className={ui.briefLines}>
            {delivery.payload.lines.map((line, i) => (
              <li key={i} className={ui.briefLine} data-tone={line.tone}>
                <span className={ui.briefDot[line.tone]} aria-hidden="true" />
                {line.text}
              </li>
            ))}
          </ul>
        )}
      {message && reply && (
        <QuickReply
          draftKey={delivery.id}
          to={message.authorName ?? firstName(null, t)}
          send={reply.send}
          onSent={reply.onSent}
          onEscape={reply.onEscape}
          onOpen={() => onAction(delivery, 'open')}
        />
      )}
      {!ask && !(message && reply) && (
        <div className={styles.cardButtons}>
          {allowed.has('accept') &&
            button(
              'accept',
              t['com.affine.pipe.director.tasks.mine.accept'](),
              true
            )}
          {allowed.has('ask') &&
            !status?.asked &&
            button('ask', t['com.affine.pipe.director.tasks.mine.ask']())}
          {button('open', openLabel(delivery, t), !task)}
        </div>
      )}
      {ask && task && (
        <AskBox
          to={task.assignedByName ?? firstName(null, t)}
          ask={ask}
          pending={pending}
        />
      )}
      <ItemNotice status={status} />
    </article>
  );
}
